import { Component, OnInit, inject, signal } from '@angular/core';
import { IBizEntity, IBizEntityType, BizEntityTypeCategory, LoggerService, IRights } from '@optima-stride-ai/core';
import { FormBizEntityComponent } from '@optima-stride-ai/optima-app-framework';
import { createNewBizEntity } from '../biz-entity.factory';
import { closeOffCanvas, OffCanvasPlacement, OptimaOffCanvasService, ToastService } from '@optima-stride-ai/optima-components';
import { OffcanvasService } from '@coreui/angular';
import { GlobalStateService } from '../../../../../service/global.service';
import { FileOperationsService } from '../../../libraries/services/file-operations.service';
import { LibraryFacadeService } from '../../../libraries/services/library-facade.service';
import { UIMessages } from '../../../../shared/enums/app-constants';

@Component({
  selector: 'app-form-add-file',
  imports: [FormBizEntityComponent],
  templateUrl: './form-add-file.component.html',
  styleUrls: ['./form-add-file.component.sass'],
})
export class FormAddFileComponent implements OnInit {
  private readonly logger = inject(LoggerService);
  private readonly global = inject(GlobalStateService);
  private readonly offCanvasFormService = inject(OptimaOffCanvasService);
  private readonly offcanvasService = inject(OffcanvasService);
  private readonly toastService = inject(ToastService);
    private readonly facade = inject(LibraryFacadeService);
  private readonly fileOps = inject(FileOperationsService);
  public selectedBizEntityId: string | null = null;
  public currentFileItem = signal<IBizEntityType | null>(null);
  public selectedTemplateData = signal<{ templateId: string; fileUrl: string; templateName: string } | null>(null);
  public bizEntity = signal<IBizEntity | null>(null);
  public isEditing = false;

  ngOnInit(): void {
    this.logger.info('📋 FormAddFileComponent initialized');
    
    // Subscribe to selected entity ID
    this.global.selectedBizEntityId$.subscribe((id) => {
      this.logger.info(`📋 Selected entity ID: ${id}`);
      this.selectedBizEntityId = id;
    });

    // Subscribe to selected template data
    this.global.selectedTemplateData$.subscribe((data) => {
      if (data) {
        this.logger.info(`📋 Template data received: ${data.templateName}`);
        this.selectedTemplateData.set(data);
      }
    });

    // Get the file type item from the off-canvas service data
    const fileTypeItem = this.offCanvasFormService.dataOfCurrentFormInView as IBizEntityType;
    this.logger.info(`📋 File type item from off-canvas: ${JSON.stringify(fileTypeItem)}`);
    
    if (fileTypeItem) {
      this.currentFileItem.set(fileTypeItem);
      this.logger.info(`📋 Set current file item: ${fileTypeItem.name}, category: ${fileTypeItem.category}`);
      
      // Initialize the bizEntity for creating a new file
      this.initializeBizEntity(fileTypeItem);
    } else {
      this.logger.warn('⚠️ No file type item received from off-canvas service');
    }

    //     this.relationshipStateStoreService.changed?.subscribe(() => {
    //   console.log('Relationship state changed, current value:', this.relationshipStateStoreService.getState());
    //   this.global.setBizEntityCreated(true);
    // })
  }

  private initializeBizEntity(fileType: IBizEntityType): void {
    this.logger.info(`📋 Initializing bizEntity for file type: ${fileType.name}, category: ${fileType.category}`);
    
    // Initialize empty bizEntity for file creation
    const newBizEntity = createNewBizEntity(
      fileType,
      this.selectedBizEntityId || '',
      'cilFile',
      ['File'],
      { tenantId: '', appId: '' }
    );

    // Set the signal to trigger change detection
    this.bizEntity.set(newBizEntity);
    
    this.logger.info(`📋 Form Add File initialized with parent: ${this.selectedBizEntityId}, type: ${fileType.name}`);
  }

  async onSaveRequested(params: { bizEntity: IBizEntity; isRelationship: boolean; relationship: any }): Promise<void> {
    const { bizEntity, isRelationship, relationship } = params;
    const item = this.currentFileItem();
    const templateData = this.selectedTemplateData();
    if (!item) {
      this.logger.warn('⚠️ Validation failed - missing item');
      this.toastService.toast('Please provide a valid file type.');
      return;
    }

    if (!bizEntity.name?.trim()) {
      this.logger.warn('⚠️ Validation failed - missing file name');
      this.toastService.toast('Please provide a file name.');
      return;
    }

    // Validate that we have a parentId
    const parentId = bizEntity.parentId || '';
    if (!parentId) {
      this.logger.error('❌ No parent folder selected');
      this.toastService.toast('Error: No parent folder selected. Please select a parent folder first.');
      return;
    }

    try {
      this.logger.info(`🔧 Creating file: ${item.name || 'unknown'}, category: ${item.category}, fileName: ${bizEntity.name}, parent: ${parentId}`);
      
      let newFile;
      
      // Check if we have template data - if yes, create from template
      if (templateData && templateData.fileUrl) {
        this.logger.info(`📄 Creating file from template: ${templateData.templateName}`);
        newFile = await this.fileOps.createFileFromTemplate(item, bizEntity.name, parentId, templateData.fileUrl, params);
        
        // Clear the template data after use
        this.global.clearSelectedTemplateData();
      } else {
        // Otherwise, create a blank file as usual
        newFile = await this.fileOps.handleNewFileCreation(item, bizEntity.name, parentId, params);
      }
      
      this.logger.info(`✅ File created successfully: ${newFile?.name || 'unknown'} (ID: ${newFile?.id || 'unknown'})`);
      
      if (!newFile || !newFile.id) {
        this.logger.error(`❌ File creation returned invalid result for ${bizEntity.name}`);
        this.toastService.toast('Failed to create file. Please try again.');
        return;
      }
      
      // Generate thumbnail if markdown (only for blank files, templates already have thumbnails)
      if (!templateData && item.category === BizEntityTypeCategory.HayagrivaFileMarkdown) {
        this.logger.info('🖼️ Generating markdown thumbnail...');
        await this.fileOps.generateMarkdownThumbnailForNewFile(
          newFile,
          '# New Document\n\nStart writing your markdown content here...\n'
        );
      }

      // Notify that entity was created and pass the file data
      const completeEntity = await this.facade.getBizEntityById(newFile.id);
      const rights = completeEntity?.rights as IRights;
      if(!rights.read){
        this.toastService.warning(UIMessages.NO_READ_ACCESS);
           closeOffCanvas(this.offCanvasFormService, this.offcanvasService, OffCanvasPlacement.End, true);
        return;
      }         
      this.global.setCreatedFileData(newFile, item.category);
      this.global.setBizEntityCreated(true);
      
      this.afterSubmissionSucceeded();
    } catch (error) {
      this.handleSubmissionError(error);
    }
  }

  private afterSubmissionSucceeded(): void {
    closeOffCanvas(this.offCanvasFormService, this.offcanvasService, OffCanvasPlacement.End, true);
    this.toastService.toast('File created successfully.');
  }

  private handleSubmissionError(error: unknown): void {
    this.logger.error(`Error creating file: ${error}`);

    if (error instanceof Error && error.message === UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD) {
      this.toastService.toast(UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD);
      return;
    }

    this.toastService.toast('Failed to create file. Please try again.');
  }
}
